import React from 'react';
import { styled } from '@mui/material/styles';
import ResourceTable, { IColumn } from '../../components/ResourceTable';

import { OrganisationAssignmentDto } from 'models/dtoTypes';
import Notice from '../../components/Notice';
import Link from 'components/Link';
import { useOrganisationAssignmentFilterSchema } from './_smartGovernanceSchema';
import {
  DeleteOrganisationAssignment,
  GetAllOrganisationAssignments,
} from 'services/organisationAssignment';
import { Chip, Stack, Typography } from '@mui/material';
import generateNonce from 'utils/generateNonce';
import { date2str } from 'utils/date';
import { getKosmosReportLink, getSgReportLink } from 'utils/report';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SmartGovernanceListPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function SmartGovernanceListPage(): JSX.Element {
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [nonce, setNonce] = React.useState('');
  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const { t } = useTranslation('default');

  const handleDeleteOrganisationAssignment = async (model: OrganisationAssignmentDto) => {
    try {
      await DeleteOrganisationAssignment(model.id);
      setNonce(generateNonce());
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: t('cannot-delete-organisation-assignment'),
        severity: 'error',
      });
    }
  };

  const generateColumns = (): IColumn<OrganisationAssignmentDto>[] => [
    {
      label: t('organisation'),
      dataIndex: 'organisationId',
      sortIndex: 'organisationId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          <Link
            to={`/organisations/${model.organisationId || ''}`}
            text={model.organisation?.name || t('unknown')}
          />
        );
      },
    },
    {
      label: t('assigner'),
      dataIndex: 'assignerId',
      sortIndex: 'assignerId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          <Stack sx={{ minWidth: 220 }}>
            {model.assigner?.email && (
              <Typography>
                <b>{t('assigner')}</b>: {model.assigner?.email}
              </Typography>
            )}
            {model.acceptUser?.email && (
              <Typography>
                <b>{t('accept')}</b>: {model.acceptUser?.email}
              </Typography>
            )}
            {Boolean(model.smeUserCount) && (
              <Typography>
                <b>{t('assignee-count')}</b>: {model.smeUserCount}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      label: t('coriolis-company'),
      dataIndex: 'kosmosCompanyId',
      sortIndex: 'kosmosCompanyId',
      render: (model: OrganisationAssignmentDto) => {
        const scoreLink = model.kosmosCompanyScore
          ? getKosmosReportLink(model.kosmosCompanyId, model.organisationId)
          : '';

        return (
          <Stack>
            <Typography>
              <b>{t('name')}</b>:{' '}
              <Link to={scoreLink} text={model.kosmosCompanyName || t('unknown')} />
            </Typography>
            <Typography>
              <b>{t('esg-score')}</b>:{' '}
              <Link to={scoreLink} text={model.kosmosCompanyScore?.toString() || ''} />
            </Typography>
          </Stack>
        );
      },
    },
    {
      label: t('sg-score'),
      dataIndex: 'manualCompany',
      sortIndex: 'manualCompany',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.esgScore) {
          return (
            <Link
              to={getSgReportLink(model.kosmosCompanyId, model.organisationId)}
              text={model?.manualCompany?.esgScore.toString() || ''}
            />
          );
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('sg-scored-at'),
      dataIndex: 'manualCompany_scoredAt',
      sortIndex: '',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.lastScoredAt) {
          return date2str(model?.manualCompany?.lastScoredAt, 'YYYY-MM-DD HH:mm:ss');
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('is-accepted'),
      sortIndex: 'isAccepted',
      render: (model) => {
        const enabled = Boolean(model.isAccepted);
        return <Chip label={enabled ? 'Yes' : 'No'} color={enabled ? 'primary' : 'default'} />;
      },
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('remove'),
          onClick: handleDeleteOrganisationAssignment,
        },
      ],
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('smart-governance-assignments')}</h2>
      </div>
      <ResourceTable
        key={nonce}
        filterSchema={useOrganisationAssignmentFilterSchema()}
        columns={generateColumns()}
        onFetch={GetAllOrganisationAssignments}
        defaultSort={{
          sortColumn: 'organisationId',
          isDesc: false,
        }}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
